/* Public cohort summary — iter86.
 * Route: /cohort-summary (no auth, no PII)
 * Anonymous dashboard displayed on the school website to reassure families:
 * "X% of monitored students improved this semester". */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  TrendUp, TrendDown, ArrowsHorizontal, GraduationCap, ShieldCheck,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicCohortSummary() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/public/cohort-summary`);
        setData(data);
      } catch (e) {
        setError('Données indisponibles pour le moment');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-[#94A3B8]">Chargement du bilan…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-sm text-[#64748B]">{error || 'Aucune donnée'}</p>
      </div>
    );
  }

  const chartData = (data.months || []).map((m) => ({
    name: `M+${m.month_index}`,
    "% amélioration": m.improved_pct,
    "% dégradation": m.degraded_pct,
  }));
  const generatedDate = data.generated_at
    ? new Date(data.generated_at).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : '';

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6"
            data-testid="public-cohort-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="text-center space-y-2">
          <GraduationCap size={48} weight="duotone"
                            className="mx-auto text-[#0052FF]" />
          <h1 className="text-3xl md:text-4xl font-outfit font-bold text-[#0F172A]">
            Notre engagement pour la réussite
          </h1>
          <p className="text-sm text-[#64748B] max-w-xl mx-auto">
            Bilan public et anonyme des plans de soutien pédagogique mis en
            place pour accompagner les élèves en difficulté. Aucune donnée
            personnelle n'est publiée — uniquement des pourcentages globaux.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-wide font-bold text-[#64748B]">
                Élèves accompagnés
              </p>
              <p className="text-4xl font-outfit font-bold text-[#0052FF] mt-1"
                data-testid="pc-total-plans">
                {data.total_plans}
              </p>
              <p className="text-[10px] text-[#94A3B8]">plans actifs ou terminés</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-800 flex items-center gap-1">
                <TrendUp size={12} weight="bold" /> Ont progressé
              </p>
              <p className="text-4xl font-outfit font-bold text-emerald-700 mt-1"
                data-testid="pc-improved-pct">
                {data.improved_pct}%
              </p>
              <p className="text-[10px] text-emerald-700/70">
                moyenne remontée d'au moins 0,5 pt
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-wide font-bold text-[#64748B] flex items-center gap-1">
                <ArrowsHorizontal size={12} weight="bold" /> Stables
              </p>
              <p className="text-4xl font-outfit font-bold text-[#64748B] mt-1">
                {data.stable_pct}%
              </p>
              <p className="text-[10px] text-[#94A3B8]">
                niveau maintenu
              </p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-wide font-bold text-red-800 flex items-center gap-1">
                <TrendDown size={12} weight="bold" /> Dégradation
              </p>
              <p className="text-4xl font-outfit font-bold text-red-700 mt-1">
                {data.degraded_pct}%
              </p>
              <p className="text-[10px] text-red-700/70">
                nécessitent un renforcement
              </p>
            </CardContent>
          </Card>
        </div>

        {chartData.length > 0 && (
          <Card>
            <CardContent className="p-5">
              <h2 className="text-xl font-outfit font-bold mb-1">
                Évolution mois après mois
              </h2>
              <p className="text-xs text-[#64748B] mb-4">
                % d'élèves qui progressent (vert) ou décrochent (rouge)
                dans les mois suivant l'activation d'un plan.
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis unit="%" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="% amélioration" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="% dégradation" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center gap-3 p-4 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE]">
          <ShieldCheck size={28} weight="duotone" className="text-[#0052FF] flex-shrink-0" />
          <div className="text-xs text-[#0052FF]/90">
            <strong>Anonymisation garantie.</strong>{' '}
            Aucun nom, classe ou identifiant d'élève n'apparaît sur cette page.
            Les statistiques sont recalculées quotidiennement à partir des
            plans de soutien anonymisés. Données générées le {generatedDate}.
          </div>
        </div>
      </div>
    </div>
  );
}
